// src/components/Admin/LeadPricingSetup/ConditionDefinitionForm.jsx
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../../../ui/button'; // Updated
import { Input } from '../../../ui/input'; // Updated
import { Label } from '../../../ui/label'; // Updated
import { Textarea } from '../../../ui/textarea'; // Updated
import { Checkbox } from '../../../ui/checkbox'; // Updated
import { Card, CardContent,  CardFooter, CardHeader, CardTitle } from '../../../ui/card'; // Updated [cite: 15]
import { cn } from '../../../../lib/utils';
import { Loader2 } from 'lucide-react'; // Import loader icon

const defaultValues = {
    condition_code: '',
    name: '',
    description: '',
    config_schema_guidance: '{}',
    applicable_question_types_filter: '[]',
    is_active: true,
};

export default function ConditionDefinitionForm({ initialData, onSave, onCancel, isLoading }) {
    const { control, handleSubmit, reset, formState: { errors, isDirty }, setError } = useForm({
        defaultValues: initialData ? {
            ...initialData,
            config_schema_guidance: JSON.stringify(initialData.config_schema_guidance || {}, null, 2),
            applicable_question_types_filter: JSON.stringify(initialData.applicable_question_types_filter || [], null, 2),
        } : defaultValues
    });

    useEffect(() => {
        if (initialData) {
            reset({
                ...initialData,
                config_schema_guidance: JSON.stringify(initialData.config_schema_guidance || {}, null, 2),
                applicable_question_types_filter: JSON.stringify(initialData.applicable_question_types_filter || [], null, 2),
            });
        } else {
            reset(defaultValues);
        }
    }, [initialData, reset]);

    const onSubmit = (data) => {
        let parsedSchemaGuidance = null;
        let parsedApplicableTypes = null;
        let formErrorsExist = false;

        try {
            // Allow empty object {} or empty content for config_schema_guidance
            if (data.config_schema_guidance && data.config_schema_guidance.trim() !== '') {
                 if (data.config_schema_guidance.trim() === '{}') {
                    parsedSchemaGuidance = {};
                 } else {
                    parsedSchemaGuidance = JSON.parse(data.config_schema_guidance);
                 }
            } // if empty or just whitespace, it remains null by default
        } catch (e) {
            setError("config_schema_guidance", { type: "manual", message: "Config Schema Guidance is not valid JSON." });
            formErrorsExist = true;
        }

        try {
            // Allow empty array [] or empty content for applicable_question_types_filter
            if (data.applicable_question_types_filter && data.applicable_question_types_filter.trim() !== '') {
                if (data.applicable_question_types_filter.trim() === '[]') {
                    parsedApplicableTypes = [];
                } else {
                    parsedApplicableTypes = JSON.parse(data.applicable_question_types_filter);
                     if (!Array.isArray(parsedApplicableTypes)) {
                        setError("applicable_question_types_filter", { type: "manual", message: "Applicable Question Types Filter must be a valid JSON array." });
                        formErrorsExist = true;
                        parsedApplicableTypes = null; // Reset if not array
                    }
                }
            } // if empty or just whitespace, it remains null
        } catch (e) {
            setError("applicable_question_types_filter", { type: "manual", message: "Applicable Question Types Filter is not valid JSON array." });
            formErrorsExist = true;
        }

        if (formErrorsExist) {
            return; 
        }

        const payload = {
            condition_code: data.condition_code,
            name: data.name,
            description: data.description || null,
            config_schema_guidance: parsedSchemaGuidance, // Can be null if field was empty
            applicable_question_types_filter: parsedApplicableTypes, // Can be null if field was empty
            is_active: data.is_active,
        };
        onSave(payload);
    };

    const errorTextClass = "text-sm text-destructive mt-1";
    const helperTextClass = "text-xs text-muted-foreground";

    return (
        <Card>
            <form onSubmit={handleSubmit(onSubmit)}>
                <CardHeader>
                    <CardTitle>{initialData ? 'Edit' : 'Create New'} Pricing Condition Definition</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div>
                        <Label htmlFor="condition_code">
                            Condition Code <span className="text-destructive">*</span> 
                        </Label>
                        <Controller
                            name="condition_code"
                            control={control}
                            rules={{ required: "Condition code is required." }}
                            render={({ field }) => (
                                <Input type="text" id="condition_code" {...field} className="mt-1" disabled={!!initialData} />
                            )}
                        />
                        <p className={cn(helperTextClass, "mt-1")}>System ID, e.g., Q_MULTI_ALL, cannot be changed after creation.</p>
                        {errors.condition_code && <p className={errorTextClass}>{errors.condition_code.message}</p>}
                    </div>

                    <div>
                        <Label htmlFor="name">Display Name <span className="text-destructive">*</span></Label>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: "Display name is required." }}
                            render={({ field }) => <Input type="text" id="name" {...field} className="mt-1" />}
                        />
                        {errors.name && <p className={errorTextClass}>{errors.name.message}</p>}
                    </div>

                    <div>
                        <Label htmlFor="description">Description (Admin Guidance)</Label>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => <Textarea id="description" {...field} value={field.value ?? ''} rows="3" className="mt-1 font-mono text-xs" />}
                        />
                    </div>

                    <div>
                        <Label htmlFor="config_schema_guidance">
                            Config Schema Guidance (JSON format)
                        </Label>
                        <Controller
                            name="config_schema_guidance"
                            control={control}
                            render={({ field }) => (
                                <Textarea 
                                    id="config_schema_guidance" 
                                    {...field} 
                                    value={field.value ?? ''}
                                    rows="5" 
                                    className="mt-1 font-mono text-xs"
                                    placeholder={'e.g., {\n  "fields_required": {\n    "key_name": "Description of key"\n  },\n  "notes": "Additional help text for admin"\n}'}
                                />
                            )}
                        />
                        <p className={cn(helperTextClass, "mt-1")}>Describes expected JSON for rules using this condition. Can be empty or {}.</p>
                        {errors.config_schema_guidance && <p className={errorTextClass}>{errors.config_schema_guidance.message}</p>}
                    </div>

                    <div>
                        <Label htmlFor="applicable_question_types_filter">
                            Applicable Question Types Filter (JSON Array)
                        </Label>
                        <Controller
                            name="applicable_question_types_filter"
                            control={control}
                            render={({ field }) => (
                                <Textarea 
                                    id="applicable_question_types_filter" 
                                    {...field} 
                                    value={field.value ?? ''}
                                    rows="3" 
                                    className="mt-1 font-mono text-xs"
                                    placeholder={'e.g., ["multi-select", "checkbox_group"]'}
                                />
                            )}
                        />
                        <p className={cn(helperTextClass, "mt-1")}>e.g., ["multi-select", "checkbox"]. Optional, for UI filtering. Can be empty or [].</p>
                        {errors.applicable_question_types_filter && <p className={errorTextClass}>{errors.applicable_question_types_filter.message}</p>}
                    </div>

                    <div className="flex items-center space-x-2 pt-2">
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    id="is_active_def" // Ensure this ID is unique if form rendered multiple times on one page, or prefix
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                            )}
                        />
                        <Label htmlFor="is_active_def" className="font-normal">Is Active</Label>
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end space-x-3 pt-6">
                    <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="default" disabled={isLoading || (!isDirty && !!initialData) }>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isLoading ? "Saving..." : (initialData ? 'Save Changes' : 'Create Definition')}
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}